// The game chart: live candles rendered by velari plus two entry-line series
// (long/short). Entry lines are NaN until a round opens, then run flat at the
// entry price — NaN gaps mean nothing is drawn outside rounds.
import { createChart, candlestickSeries, lineSeries, type VelariChart } from 'velari';
import { chartTheme, registerCharts } from '../core/theme';
import type { MarketFrameDto, MarketSeedDto } from './types';

const WINDOW_POINTS = 240;

export class GameChart {
  private chart: VelariChart | null = null;
  private readonly host: HTMLElement;
  private lastFrame: MarketFrameDto | null = null;
  private buyEntry = Number.NaN;
  private sellEntry = Number.NaN;

  constructor(host: HTMLElement) {
    this.host = host;
  }

  /** (Re)builds the chart from a seed frame — first frame of every stream. */
  reset(seed: MarketSeedDto): void {
    this.chart?.destroy();
    const gaps = new Array<number>(seed.x.length).fill(Number.NaN);
    this.chart = createChart(this.host, {
      data: {
        x: Float64Array.from(seed.x),
        series: [
          {
            name: 'AMGA',
            type: candlestickSeries,
            values: {
              open: Float64Array.from(seed.open),
              high: Float64Array.from(seed.high),
              low: Float64Array.from(seed.low),
              close: Float64Array.from(seed.close),
            },
          },
          // priceLine draws the dashed entry level with a pill label while a
          // round is open (the last row holds the entry price, NaN otherwise)
          { name: 'Long', type: lineSeries, values: [...gaps], color: '#30d158', width: 1.5, priceLine: true },
          { name: 'Short', type: lineSeries, values: [...gaps], color: '#ff453a', width: 1.5, priceLine: true },
        ],
      },
      xType: 'time',
      maxPoints: WINDOW_POINTS,
      rightOffset: 5,
      legend: false,
      theme: chartTheme(),
      yFormat: (v) => `$${v.toFixed(2)}`,
    });
    registerCharts(this.chart);
  }

  /** Streams one tick; same bar time reshapes the forming candle in place. */
  tick(frame: MarketFrameDto): void {
    this.lastFrame = frame;
    this.chart?.update(frame.x, [frame.open, frame.high, frame.low, frame.close, this.buyEntry, this.sellEntry]);
  }

  /** Starts drawing the entry line at the given price (and repaints the current bar). */
  markEntry(direction: 'buy' | 'sell', price: number): void {
    this.buyEntry = direction === 'buy' ? price : Number.NaN;
    this.sellEntry = direction === 'sell' ? price : Number.NaN;
    if (this.lastFrame) this.tick(this.lastFrame);
  }

  /** Stops extending the entry line (history scrolls out with the window). */
  clearEntry(): void {
    this.buyEntry = Number.NaN;
    this.sellEntry = Number.NaN;
  }
}
